/**
 * 智能体 订单管理 管理器
 * 该管理器用于管理智能体订单相关的业务逻辑
 * @author Joey
 */

import { useI18nNavigate } from "@/hooks/use-i18n-navigate";
import { useToast } from "@/hooks/use-toast";
import { canUseWindow } from "@/utils/canuseWindow";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { agentPermissionManager } from "./agent-dashboard-permission-manager";
import { OrderUserData, orderUserList } from "@/services/agent-v2-order-user-list";
import { useTranslation } from "react-i18next";
import { orderAttachmentUpload } from "@/services/agent-v2-order-attachment-upload";
import { createOrderUser } from "@/services/agent-v2-create-order-user";
import { agentV2OrderStats, AgentV2OrderStatsData } from "@/services/agent-v2-order-stats";
import { orderSeatList, OrderSeatData } from "@/services/agent-v2-order-seat-list";
import { downloadFile } from "@/utils/download";
import { userManagementManager, useUserManagementHooks } from "./agent-dashboard-user-management-manager";
import { cosUploadFile } from "@/services/cos-upload-file";

export interface SearchFormData {
  searchType: string | null;
  searchValue: string;
  goodsId: string | null;
  page: number;
  pageSize: number;
  total?: number;
}

export interface CreateOrderFormData {
  userName: string;
  email: string;
  goodsId: string;
  description?: string;
  orderAttachment?: string;
  orderAttachmentName?: string;
  orderAttachmentType?: string;
}

type OrderListChangeEventDetail = { orderUsers: OrderUserData[] };
type OrderListChangeEvent = CustomEvent<OrderListChangeEventDetail>;

type SearchFormChangeEventDetail = SearchFormData
type SearchFormChangeEvent = CustomEvent<SearchFormChangeEventDetail>;

type CreateOrderFormChangeEventDetail = CreateOrderFormData
type CreateOrderFormChangeEvent = CustomEvent<CreateOrderFormChangeEventDetail>;

type OrderStatsChangeEventDetail = AgentV2OrderStatsData
type OrderStatsChangeEvent = CustomEvent<OrderStatsChangeEventDetail>;

type OrderDetailChangeEventDetail = OrderUserData | null
type OrderDetailChangeEvent = CustomEvent<OrderDetailChangeEventDetail>;

type OrderSeatListChangeEventDetail = {
  orderSeatListForSearch: OrderSeatData[];
  orderSeatListForCreate: OrderSeatData[];
};
type OrderSeatListChangeEvent = CustomEvent<OrderSeatListChangeEventDetail>;

export class AgentOrderManager {
  private static instance: AgentOrderManager;
  private readonly STORAGE_KEY = "MIUTA_AGENT_ORDER_LIST";
  // 当前智能体ID
  private agentId: string = "";
  // 订单列表
  private orderUsers: OrderUserData[] = [];
  /** 搜索栏商品下拉：不传 status，hasSales=true */
  private orderSeatListForSearch: OrderSeatData[] = [];
  /** 创建订单弹窗：status=1，不传 hasSales */
  private orderSeatListForCreate: OrderSeatData[] = [];
  // 订单详情数据
  private orderDetail: OrderUserData | null = null;
  // 订单统计数据
  private orderStats: AgentV2OrderStatsData = {
    totalOrderCount: 0,
    completedOrderCount: 0,
    totalAmount: 0,
  }
  // 统计数据初始化状态
  private initializeStats = false;
  // 搜索表单的数据
  private searchForm: SearchFormData = {
    searchType: "",
    searchValue: "",
    goodsId: null,
    page: 1,
    pageSize: 10,
    total: 0,
  }
  // 创建订单的数据
  private createFormData: CreateOrderFormData = {
    userName: "",
    email: "",
    goodsId: "",
    description: "",
    orderAttachment: "",
    orderAttachmentName: "",
    orderAttachmentType: "",
  };

  private constructor() { }

  // 单例模式
  public static getInstance(): AgentOrderManager {
    if (!AgentOrderManager.instance) {
      AgentOrderManager.instance = new AgentOrderManager();
    }
    return AgentOrderManager.instance;
  }

  // 初始化
  public async init(agentId: string) {
    this.setAgentId(agentId);
    try {
      // 初始化时，如果列表为空，才请求数据
      if (this.getOrderUsers().length === 0) {
        await this.fetchOrderList();
      }
    } catch (error) {
      console.error("Failed to init agent permission list cache:", error);
      throw error;
    }
  }

  // 请求订单列表数据
  public async fetchOrderList(page?: number) {
    try {
      const data = await orderUserList({
        ...this.getSearchForm(),
        agentId: this.agentId,
        page: page ?? this.searchForm.page,
      });
      this.setOrderUsers(data.list);
      this.setSearchForm({
        ...this.searchForm,
        page: data.page,
        pageSize: data.pageSize,
        total: data.total,
      });
    } catch (error) {
      console.error("Failed to fetch agent order list:", error);
      this.dispatchOrderUsersChange();
      throw error;
    }
  }

  /** 搜索栏用席位商品列表（不传 status，hasSales 默认 true） */
  public async fetchOrderSeatListForSearch() {
    try {
      const data = await orderSeatList({
        agentId: this.agentId,
        page: 1,
        pageSize: 100,
        hasSales: true,
      });
      this.setOrderSeatListForSearch(data.list);
    } catch (error) {
      console.error("Failed to fetch agent order seat list (search):", error);
      this.dispatchOrderSeatListChange();
      throw error;
    }
  }

  /** 创建订单弹窗用席位商品列表（status=1，不传 hasSales） */
  public async fetchOrderSeatListForCreate() {
    try {
      const data = await orderSeatList({
        agentId: this.agentId,
        page: 1,
        pageSize: 100,
        status: 1,
      });
      this.setOrderSeatListForCreate(data.list);
    } catch (error) {
      console.error("Failed to fetch agent order seat list (create):", error);
      this.dispatchOrderSeatListChange();
      throw error;
    }
  }

  public async fetchOrderSeatLists() {
    await Promise.all([
      this.fetchOrderSeatListForSearch(),
      this.fetchOrderSeatListForCreate(),
    ]);
  }

  // 请求订单统计数据
  public async fetchOrderStats(agentId?: string) {
    try {
      const data = await agentV2OrderStats(agentId || this.agentId);
      this.setOrderStats(data);
    } catch (error) {
      console.error("Failed to fetch agent order stats:", error);
      this.dispatchOrderStatsChange();
      throw error;
    }
  }

  // 创建订单
  public async createOrder(formData: CreateOrderFormData) {
    try {
      await createOrderUser({
        agentId: this.agentId,
        ...formData,
      });
    } catch (error) {
      console.error("Failed to create order:", error);
      throw error;
    }
  }

  // 上传订单附件
  public async uploadOrderAttachment(file: File) {
    try {
      const res = await cosUploadFile(file, "common");
      this.setCreateFormData({
        ...this.getCreateFormData(),
        orderAttachment: res.url,
        orderAttachmentName: file.name,
        orderAttachmentType: file.name.split('.').pop(), // 取文件扩展名
      });
    } catch (error) {
      console.error("Failed to upload order attachment:", error);
      throw error;
    }
  }

  // 重置列表和搜索表单
  public resetOrderListAndSearchForm() {
    this.setOrderUsers([]);
    this.setSearchForm({
      searchType: '',
      searchValue: '',
      goodsId: null,
      page: 1,
      pageSize: 10,
    });
    this.setOrderSeatListForSearch([]);
    this.setOrderSeatListForCreate([]);
    // 重置统计数据
    this.setInitializeStats(false);
  }

  // ==================== 读取写入操作 ====================
  public getAgentId() {
    return this.agentId;
  }
  public setAgentId(agentId: string) {
    this.agentId = agentId;
  }

  public getOrderUsers() {
    return this.orderUsers;
  }
  private setOrderUsers(list: OrderUserData[]) {
    this.orderUsers = list;
    this.dispatchOrderUsersChange();
  }

  public getSearchForm() {
    return this.searchForm;
  }
  public setSearchForm(form: SearchFormData) {
    this.searchForm = form;
    this.dispatchSearchFormChange();
  }

  public getCreateFormData() {
    return this.createFormData;
  }
  public setCreateFormData(form: CreateOrderFormData) {
    this.createFormData = form;
    this.dispatchCreateOrderFormChange();
  }

  public getOrderStats() {
    return this.orderStats;
  }
  private setOrderStats(stats: AgentV2OrderStatsData) {
    this.orderStats = stats;
    this.dispatchOrderStatsChange();
  }

  public getOrderDetail() {
    return this.orderDetail;
  }
  public setOrderDetail(order: OrderUserData | null) {
    this.orderDetail = order;
    this.dispatchOrderDetailChange();
  }

  public getOrderSeatListForSearch() {
    return this.orderSeatListForSearch;
  }
  private setOrderSeatListForSearch(list: OrderSeatData[]) {
    this.orderSeatListForSearch = list;
    this.dispatchOrderSeatListChange();
  }

  public getOrderSeatListForCreate() {
    return this.orderSeatListForCreate;
  }
  private setOrderSeatListForCreate(list: OrderSeatData[]) {
    this.orderSeatListForCreate = list;
    this.dispatchOrderSeatListChange();
  }

  public getInitializeStats() {
    return this.initializeStats;
  }
  public setInitializeStats(initializeStats: boolean) {
    this.initializeStats = initializeStats;
  }

  // ==================== 订阅通知操作 ====================
  // 监听订单列表变化事件
  private dispatchOrderUsersChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<OrderListChangeEventDetail>(this.STORAGE_KEY, {
      detail: { orderUsers: this.orderUsers }
    });
    window.dispatchEvent(event);
  }
  public onOrderUsersChange(listener: (e: OrderListChangeEvent) => void) {
    if (!canUseWindow()) return;
    window.addEventListener(this.STORAGE_KEY, listener);
    return () => window.removeEventListener(this.STORAGE_KEY, listener);
  }

  //  监听搜索表单变化
  private dispatchSearchFormChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<SearchFormChangeEventDetail>(`${this.STORAGE_KEY}:search-form-change`, {
      detail: this.searchForm
    });
    window.dispatchEvent(event);
  }
  public onSearchFormChange(listener: (e: SearchFormChangeEvent) => void) {
    const eventName = `${this.STORAGE_KEY}:search-form-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听创建订单表单变化
  private dispatchCreateOrderFormChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<CreateOrderFormChangeEventDetail>(`${this.STORAGE_KEY}:create-form-change`, {
      detail: this.createFormData
    });
    window.dispatchEvent(event);
  }
  public onCreateOrderFormChange(listener: (e: CreateOrderFormChangeEvent) => void) {
    const eventName = `${this.STORAGE_KEY}:create-form-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听订单统计数据变化
  private dispatchOrderStatsChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<OrderStatsChangeEventDetail>(`${this.STORAGE_KEY}:order-stats-change`, {
      detail: this.orderStats
    });
    window.dispatchEvent(event);
  }
  public onOrderStatsChange(listener: (e: OrderStatsChangeEvent) => void) {
    const eventName = `${this.STORAGE_KEY}:order-stats-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听订单详情数据变化
  private dispatchOrderDetailChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<OrderDetailChangeEventDetail>(`${this.STORAGE_KEY}:order-detail-change`, {
      detail: this.orderDetail
    });
    window.dispatchEvent(event);
  }
  public onOrderDetailChange(listener: (e: OrderDetailChangeEvent) => void) {
    const eventName = `${this.STORAGE_KEY}:order-detail-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听席位商品列表变化
  private dispatchOrderSeatListChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<OrderSeatListChangeEventDetail>(`${this.STORAGE_KEY}:order-seat-list-change`, {
      detail: {
        orderSeatListForSearch: this.orderSeatListForSearch,
        orderSeatListForCreate: this.orderSeatListForCreate,
      },
    });
    window.dispatchEvent(event);
  }
  public onOrderSeatListChange(listener: (e: OrderSeatListChangeEvent) => void) {
    const eventName = `${this.STORAGE_KEY}:order-seat-list-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }
}

// ============= Hooks =============
// 订单列表数据
const useOrderListData = () => {
  const { id } = useParams();
  const toast = useToast();

  const [orderUsers, setOrderUsers] = useState(agentOrderManager.getOrderUsers());
  const [searchFormData, setSearchFormData] = useState(agentOrderManager.getSearchForm());
  const [loading, setLoading] = useState(false);

  useUserManagementHooks.useListenerAfterAction('order');

  const init = async () => {
    if (!id) {
      toast.toast({
        description: `Agent id is required`,
      });
      return;
    }
    try {
      setLoading(true);
      await agentOrderManager.init(id);
      setLoading(false);
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  const search = async (page?: number) => {
    setLoading(true);
    try {
      await agentOrderManager.fetchOrderList(page);
    } catch (error) {
      toast.showToastByCode(error);
    }
    setLoading(false);
  }

  const changeSearchForm = (key: keyof SearchFormData, value: string | number) => {
    agentOrderManager.setSearchForm({
      ...searchFormData,
      [key]: value,
    });
  }

  const changePage = (page: number) => {
    changeSearchForm('page', page);
  }

  const resetAndSearch = async () => {
    agentOrderManager.setSearchForm({
      searchType: null,
      searchValue: "",
      goodsId: null,
      page: 1,
      pageSize: 10,
    });
    await search();
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    const unsubscribe = agentOrderManager.onOrderUsersChange((e) => {
      setOrderUsers(e.detail.orderUsers)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = agentOrderManager.onSearchFormChange((e) => {
      setSearchFormData(e.detail);
    })
    return unsubscribe
  }, [])

  return {
    loading,
    orderUsers,
    searchFormData,

    search,
    changeSearchForm,
    resetAndSearch,
    changePage,
  }
}

// 席位商品列表
const useOrderSeatList = () => {
  const { id } = useParams();
  const toast = useToast();

  const [orderSeatListForSearch, setOrderSeatListForSearchState] = useState(
    agentOrderManager.getOrderSeatListForSearch()
  );
  const [orderSeatListForCreate, setOrderSeatListForCreateState] = useState(
    agentOrderManager.getOrderSeatListForCreate()
  );
  const [loading, setLoading] = useState(false);

  const init = async () => {
    if (!id) {
      toast.toast({ description: `Agent id is required` });
      return;
    }
    try {
      setLoading(true);
      agentOrderManager.setAgentId(id);
      await agentOrderManager.fetchOrderSeatLists();
    } catch (error) {
      console.log(error);
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    const unsubscribe = agentOrderManager.onOrderSeatListChange((e) => {
      setOrderSeatListForSearchState(e.detail.orderSeatListForSearch);
      setOrderSeatListForCreateState(e.detail.orderSeatListForCreate);
    });
    return unsubscribe;
  }, []);

  return {
    loading,
    orderSeatListForSearch,
    orderSeatListForCreate,
  };
};

// 订单详情数据
const useOrderDetail = () => {
  const [orderDetail, setOrderDetail] = useState(agentOrderManager.getOrderDetail());

  useEffect(() => {
    const unsubscribe = agentOrderManager.onOrderDetailChange((e) => {
      setOrderDetail(e.detail);
    })
    return unsubscribe
  }, [])

  const selectedOrderDetail = (order: OrderUserData) => {
    agentOrderManager.setOrderDetail(order)
  }

  const resetOrderDetail = () => {
    agentOrderManager.setOrderDetail(null);
  }

  return {
    orderDetail,
    selectedOrderDetail,
    resetOrderDetail,
  }
}

// 订单统计(总订单数/未完成订单数/总交易额)
const useOrderStats = () => {
  const toast = useToast();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [orderStatsData, setOrderStatsData] = useState(agentOrderManager.getOrderStats());

  const init = async () => {
    if (!id) {
      toast.toast({
        description: `Agent id is required`,
      });
      return;
    }
    agentOrderManager.setAgentId(id);
    try {
      setLoading(true);
      // 初始化统计数据
      agentOrderManager.setInitializeStats(true);
      await agentOrderManager.fetchOrderStats(id);
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // 如果已经初始化了统计数据，就不需要再初始化
    if (agentOrderManager.getInitializeStats()) return;
    init();
  }, []);

  useEffect(() => {
    const unsubscribe = agentOrderManager.onOrderStatsChange((e) => {
      setOrderStatsData(e.detail)
    })
    return unsubscribe
  }, [])

  return {
    loading,
    orderStatsData,
  }
}

// 创建订单
const useCreateOrder = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const [createFormData, setCreateFormData] = useState(agentOrderManager.getCreateFormData());
  const {
    emailError,
    goodsIdError,
    resetError,
    validateField,
    validateAll
  } = useValidateFormData();

  // 修改表单数据
  const changeValue = (key: keyof CreateOrderFormData, value: string | number | string[]) => {
    // 输入即校验数据
    validateField(key, value);

    // 移除订单附件
    if (key === 'orderAttachment' && !value) {
      agentOrderManager.setCreateFormData({
        ...createFormData,
        orderAttachment: '',
        orderAttachmentName: '',
        orderAttachmentType: '',
      });
      return;
    }

    agentOrderManager.setCreateFormData({
      ...createFormData,
      [key]: value,
    });
  }

  // 校验数据
  const validate = (): boolean => {
    // 调接口前的兜底校验
    const valid = validateAll(createFormData);
    if (!valid) return false;

    return true;
  }

  // 创建订单
  const createOrder = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      await agentOrderManager.createOrder(createFormData);
      // 刷新订单列表
      Promise.all([
        agentOrderManager.fetchOrderList(),
        agentOrderManager.fetchOrderStats(),
        // 刷新智能体用户列表
        userManagementManager.fetchAgentUsersList()
      ])
      return true
    } catch (error) {
      toast.showToastByCode(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  // 清除表单数据
  const clearCreateFormData = () => {
    agentOrderManager.setCreateFormData({
      userName: '',
      email: '',
      goodsId: '',
      description: '',
      orderAttachment: '',
      orderAttachmentName: '',
      orderAttachmentType: '',
    });
    resetError();
  }

  useEffect(() => {
    const unsubscribe = agentOrderManager.onCreateOrderFormChange((e) => {
      setCreateFormData(e.detail);
    });
    return unsubscribe
  }, []);

  return {
    loading,
    createFormData,

    emailError,
    goodsIdError,
    resetError,

    createOrder,
    changeValue,
    clearCreateFormData,
  }
}

// 上传订单附件
const useUploadOrderAttachment = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  // 上传订单附件
  const uploadAttachment = async (file: File) => {
    setLoading(true);
    try {
      await agentOrderManager.uploadOrderAttachment(file);
    } catch (error) {
      toast.showToastByCode(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  return {
    uploadLoading: loading,
    uploadAttachment,
  }
}

// 校验表单数据
const useValidateFormData = () => {
  const { t } = useTranslation();
  const [emailError, setEmailError] = useState('');
  const [goodsIdError, setGoodsIdError] = useState('');

  // 校验 email
  const validateEmail = (value: string | number | string[]) => {
    const str = Array.isArray(value) ? value.join('') : String(value ?? '');
    // 检查格式：邮箱中必须包含@符号、@符号前后必须有字符、域名部分必须包含至少一个点、不允许包含空白字符
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(str)) {
      setEmailError(t('agent_dashboard.order_management.create_modal.email_help_text'));
      return false;;
    } else {
      setEmailError('');
      return true;
    }
  }

  // 校验商品
  const validateGoodsId = (value: string | number | string[]) => {
    const str = value === null || value === undefined ? '' : String(value);
    if (!str) {
      setGoodsIdError(t('agent_dashboard.order_management.create_modal.goods_info_placeholder'));
      return false;
    } else {
      setGoodsIdError('');
      return true;
    }
  }

  // 重置校验错误
  const resetError = () => {
    setEmailError('');
    setGoodsIdError('');
  }

  const validateMap: Partial<
    Record<keyof CreateOrderFormData, (value: string | number | string[]) => boolean | void>
  > = {
    email: validateEmail,
    goodsId: validateGoodsId,
  }

  const validateField = (name: keyof CreateOrderFormData, value: string | number | string[]) => {
    if (!validateMap[name]) return;
    return validateMap[name](value);
  }

  // 校验全部表单数据
  const validateAll = (formData: CreateOrderFormData) => {
    return [
      validateEmail(formData.email),
      validateGoodsId(formData.goodsId),
    ].every(Boolean);
  }

  return {
    emailError,
    goodsIdError,

    validateMap,
    validateField,
    validateAll,
    resetError,
  }
}

// 下载附件
const useDownloadAttachment = () => {
  const [loading, setLoading] = useState(false);

  const downLoad = async (url: string, fileName?: string) => {
    setLoading(true);
    try {
      await downloadFile(url, fileName);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  return {
    loading,
    downLoad,
  }

}

export const agentOrderHooks = {
  useOrderListData,
  useOrderSeatList,
  useOrderDetail,
  useOrderStats,
  useCreateOrder,
  useUploadOrderAttachment,
  useDownloadAttachment,
}

// 导出单例实例
export const agentOrderManager = AgentOrderManager.getInstance();
